import { useContext } from 'react'
import { Link } from 'gatsby'
import { ProductContext } from 'src/components/restructure/product/contexts'
import { sendEvent, makeEventTrack } from 'src/utils/restructure/analytics'
import StarRating from 'src/components/StarRating'
import { slugify } from 'src/utils/slugfy'
import { normalizeTitle } from 'src/utils/formatted'
import { Statsig } from 'src/sdk/statsig'
import { REVIEWS_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'

import { WishListButton } from './WishListButton'
import FakeSalesCounter from '../../../ProductCard/FakeSalesCounter'

interface ProductTitleProps {
  isAlbum?: boolean
  showProductCode?: boolean
  isFirstFoldRevampEnabled?: boolean
}

const STYLES = {
  firstFold: {
    container: 'flex items-start justify-between gap-10',
    content: 'min-w-0 flex-1',
    title:
      "text-restructure-primary mb-0 mt-0 font-['Inter'] text-[16px] font-semibold leading-[140%] tracking-[-0.16px]",
    meta: 'mt-1 flex items-center gap-1.5',
    brand: 'text-[12px] font-normal leading-none text-restructure-primary',
    reviewButton: 'flex items-center gap-1',
    reviewText: 'text-[12px] leading-[21px] text-restructure-primary',
    wishlistWrapper: 'shrink-0',
  },
  default: {
    container: 'flex justify-between items-start mt-[0px] sm:mt-[0px]',
    content: 'flex flex-col items-start gap-1.5 self-stretch',
    title:
      "desktop-body-regular-text1 flex flex-row text-restructure-primary mb-0 mt-0 font-['Inter'] text-[24px] font-semibold leading-[140%]",
    brandBase: "desktop-body-regular-text3 font-['Inter'] leading-[100%]",
    brandAlbum: 'text-primaryBlue text-sm font-semibold',
    brandDefault: 'text-restructure-primary !text-[18px] font-medium',
    detailsRow: 'flex flex-row justify-between',
    reviewButton: 'flex items-center gap-xxs mt-xs',
    reviewText: 'text-[14px] text-restructure-secondary',
    reviewTotal: 'text-[14px] text-restructure-action',
    productRef:
      'desktop-caption-regular text-tertiary items-center content-center mt-[8px]',
  },
} as const

export function ProductTitle({
  isAlbum = false,
  showProductCode = true,
  isFirstFoldRevampEnabled = false,
}: ProductTitleProps) {
  const { product, currentSku } = useContext(ProductContext)

  const { name, manufacturerCode } = currentSku
  const { brand, review } = product

  const isReviewDisplayExperimentEnabled = Statsig.useStatsigFeatureGateValue(
    REVIEWS_EXPERIMENT_ID
  )

  const handleClick = () => {
    const eventTrack = makeEventTrack({
      eventAction: 'ver todas as avaliações',
      eventPage: 'pagina de produto',
    })

    sendEvent(eventTrack)

    const sectionCustomerElement = isReviewDisplayExperimentEnabled
      ? document.querySelector('[aria-label="Seção Avaliações do produto"]')
      : document?.getElementById('customer_reviews_section')

    sectionCustomerElement?.scrollIntoView({
      block: isReviewDisplayExperimentEnabled ? 'center' : 'start',
      inline: 'nearest',
    })
  }

  const reviewAverageNote = review?.totalRatingsAverageNote ?? 0

  if (isFirstFoldRevampEnabled && !isAlbum) {
    return (
      <section className={STYLES.firstFold.container}>
        <div className={STYLES.firstFold.content}>
          <h1 className={STYLES.firstFold.title} data-test-id="product-name">
            {name}
          </h1>
          <div className={STYLES.firstFold.meta}>
            <p className={STYLES.firstFold.brand} data-test-id="product-brand">
              <Link to={`/marcas/${slugify(brand)}`}>
                {normalizeTitle(brand)}
              </Link>
            </p>
            {reviewAverageNote > 0 && (
              <button
                className={STYLES.firstFold.reviewButton}
                onClick={handleClick}
              >
                <StarRating
                  rating={reviewAverageNote}
                  width={11}
                  height={12}
                  maxStars={5}
                  color="#101010"
                />
                <p
                  className={STYLES.firstFold.reviewText}
                  data-testid="ratings-average-note"
                >
                  {`${reviewAverageNote.toFixed(1)}`}
                </p>
              </button>
            )}
          </div>
        </div>
        <div className={STYLES.firstFold.wishlistWrapper}>
          <WishListButton
            mobileSize="18"
            desktopSize="18"
            variant="pdp"
            isFirstFoldRevampEnabled={isFirstFoldRevampEnabled}
          />
        </div>
      </section>
    )
  }

  const brandClassName = `${STYLES.default.brandBase} ${
    isAlbum ? STYLES.default.brandAlbum : STYLES.default.brandDefault
  }`

  return (
    <section className={STYLES.default.container}>
      <div>
        <div className={STYLES.default.content}>
          <p className={brandClassName} data-test-id="product-brand">
            <Link to={`/marcas/${slugify(brand)}`}>
              {normalizeTitle(brand)}
            </Link>
          </p>
          <h1 className={STYLES.default.title} data-test-id="product-name">
            {name}
          </h1>
        </div>
        <div className={STYLES.default.detailsRow}>
          {!isAlbum && reviewAverageNote > 0 && (
            <button
              className={STYLES.default.reviewButton}
              onClick={handleClick}
            >
              <StarRating
                rating={reviewAverageNote}
                width={11}
                height={12}
                maxStars={5}
              />
              <p
                className={STYLES.default.reviewText}
                data-testid="ratings-average-note"
              >
                {`${reviewAverageNote.toFixed(1)}`}
              </p>
              <span
                className={STYLES.default.reviewTotal}
                data-testid="total-reviews"
              >
                {`(${review?.totalReviews} avaliações)`}
              </span>
            </button>
          )}
          {!isAlbum && (
            <FakeSalesCounter productId={product.productId} mode="pdp" />
          )}
          {!isAlbum && showProductCode && (
            <p className={STYLES.default.productRef}>REF: {manufacturerCode}</p>
          )}
        </div>
      </div>
    </section>
  )
}
